import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/feature/SEOHead';
import ExercicioGratisNavbar from '../components/ExercicioGratisNavbar';
import LeadCaptureForm from '../components/LeadCaptureForm';

export default function SaudeBemEstarPage() {
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);

  const areas = [
    { id: 'energia', label: 'Energia Vital', icon: 'ri-flashlight-line', color: 'bg-orange-100 text-orange-600' },
    { id: 'sono', label: 'Sono Reparador', icon: 'ri-moon-line', color: 'bg-indigo-100 text-indigo-600' },
    { id: 'alimentacao', label: 'Alimentação Saudável', icon: 'ri-restaurant-line', color: 'bg-green-100 text-green-600' },
    { id: 'exercicio', label: 'Exercício Físico', icon: 'ri-run-line', color: 'bg-blue-100 text-blue-600' },
    { id: 'mental', label: 'Saúde Mental', icon: 'ri-brain-line', color: 'bg-purple-100 text-purple-600' },
    { id: 'emocional', label: 'Equilíbrio Emocional', icon: 'ri-heart-pulse-line', color: 'bg-pink-100 text-pink-600' },
    { id: 'peso', label: 'Peso Ideal', icon: 'ri-scales-3-line', color: 'bg-teal-100 text-teal-600' },
    { id: 'vitalidade', label: 'Vitalidade e Longevidade', icon: 'ri-leaf-line', color: 'bg-lime-100 text-lime-600' },
  ];

  const toggleArea = (id: string) => setSelectedAreas(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <SEOHead
        title="Visualização de Saúde e Bem-Estar Grátis | Vitalidade — Carta do Eu Futuro"
        description="Experimente gratuitamente a Visualização de Saúde e Bem-Estar. Manifeste saúde plena e vitalidade através da conexão mente-corpo e técnicas de PNL."
        keywords="saúde bem-estar, visualização, vitalidade, PNL grátis, conexão mente-corpo"
        canonical="/exercicio-gratis/saude-bem-estar"
      />
      <ExercicioGratisNavbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10 text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold mb-6"><i className="ri-gift-line"></i>VERSÃO GRATUITA</div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent leading-tight">Visualização de Saúde e Bem-Estar</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">Conecte-se com seu corpo ideal e manifeste saúde plena através da visualização criativa e conexão mente-corpo</p>
        </div>
      </section>

      {/* O que é */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">O que é a Visualização de Saúde?</h2>
          <p className="text-lg leading-relaxed mb-6 text-gray-700">A <strong>Visualização de Saúde e Bem-Estar</strong> utiliza a conexão mente-corpo para promover cura, vitalidade e equilíbrio em todos os níveis do seu ser.</p>
          <p className="text-lg leading-relaxed text-gray-700">Estudos científicos comprovam que a visualização ativa as mesmas áreas cerebrais que a experiência real, criando mudanças fisiológicas mensuráveis no corpo.</p>
        </div>
      </section>

      {/* Exercício */}
      <section className="py-16 px-4 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold mb-4"><i className="ri-gift-line"></i>EXPERIMENTE GRÁTIS</div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Exercício Introdutório</h2>
            </div>
            <h3 className="text-xl font-bold mb-6 text-gray-900">Selecione as áreas de bem-estar que você quer melhorar:</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {areas.map((area) => (
                <button key={area.id} onClick={() => toggleArea(area.id)} className={`p-4 rounded-xl border-2 transition-all text-left cursor-pointer ${selectedAreas.includes(area.id) ? 'border-orange-500 bg-orange-50 scale-105' : 'border-gray-200 bg-white hover:border-orange-300'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${area.color} rounded-lg flex items-center justify-center`}><i className={`${area.icon} text-xl`}></i></div>
                    <span className="font-semibold text-gray-900">{area.label}</span>
                    {selectedAreas.includes(area.id) && <i className="ri-checkbox-circle-fill text-orange-500 text-xl ml-auto"></i>}
                  </div>
                </button>
              ))}
            </div>
            {selectedAreas.length > 0 && <div className="p-4 bg-orange-50 rounded-xl border border-orange-200 mb-6"><p className="text-sm text-orange-700 font-medium">Você selecionou {selectedAreas.length} área{selectedAreas.length > 1 ? 's' : ''} para transformar!</p></div>}
            {/* Bloqueados */}
            <div className="relative mt-6">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/60 to-white backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 text-white p-8 rounded-2xl text-center max-w-sm">
                  <i className="ri-vip-crown-line text-5xl mb-4 block"></i>
                  <h3 className="text-2xl font-bold mb-3">Desbloqueie o Exercício Completo</h3>
                  <p className="text-orange-100 mb-6">6 passos completos + áudio guiado + workbook</p>
                  <Link to="/planos" className="bg-white text-orange-600 px-8 py-4 rounded-xl font-bold hover:bg-orange-50 transition-all whitespace-nowrap block">Ver Planos — A partir de R$ 18</Link>
                </div>
              </div>
              <div className="opacity-30 pointer-events-none space-y-4">
                {[2,3,4,5,6].map((n) => <div key={n} className="p-6 bg-gray-50 rounded-xl border-2 border-gray-200"><h3 className="font-bold text-gray-600">Passo {n} — bloqueado</h3></div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <LeadCaptureForm exercicioNome="Saúde e Bem-Estar" />

      {/* CTA Final */}
      <section className="py-20 px-4 bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-12 h-px bg-gold-500/30 mx-auto mb-8"></div>
          <i className="ri-heart-pulse-fill text-5xl mb-6 opacity-60 block text-gold-400"></i>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-6 text-white">Pronto para Manifestar Saúde Plena e Vitalidade?</h2>
          <p className="text-xl text-white/55 mb-8 max-w-2xl mx-auto font-['Montserrat'] font-light">Desbloqueie o exercício completo e comece sua transformação em saúde hoje</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/planos" className="bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 px-10 py-5 rounded-xl font-bold hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap font-['Montserrat'] tracking-wide">Ver Planos e Preços</Link>
            <Link to="/" className="bg-white/8 text-white px-10 py-5 rounded-xl font-bold hover:bg-white/14 transition-all border border-gold-500/20 whitespace-nowrap font-['Montserrat'] tracking-wide">Voltar ao Início</Link>
          </div>
          <p className="text-white/30 text-sm mt-6 font-['Montserrat']">Garantia de 7 dias · Acesso vitalício</p>
        </div>
      </section>
    </div>
  );
}
